import { useEffect, useState, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { CircularProgress, useMediaQuery } from "@mui/material";
import NoDataList from "../../../../components/NoDataList";
import Button from "../../../../components/Button";
import ProfileCard from "../../../../components/ProfileCard";
import { images } from "../../../../constants/images";
import { MyPublicationsProps } from "../../../../interfaces/components";
import styles from "./entryPoint.module.scss";
import { useProfileRole, useToast } from "../../../../helpers/hooks";
import { addPointsInText } from "../../../../helpers/app";
import SettingsIcon from '@mui/icons-material/Settings';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Pagination from "../../../../components/Pagination";
import Modal from "../../../../components/Modal";
import Stepper from "../../../../components/Stepper/Stepper";
import MyPublicationCard from "../../../../components/MyPublicationCard";
import DeletePublicationModal from "../../../../components/DeletePublicationModal";

const EntryPoint = ({
    user,
    getMyPublicationsRequest,
    myPublications,
    pause,
    deletePublication,
    deleteFetching,
    pausePublication,
    pauseFetching
}: MyPublicationsProps) => {
    const toast = useToast();
    const navigate = useNavigate();
    const profileRole = useProfileRole();
    const params = useParams();
    const location = useLocation();
    const [page, setPage] = useState<number>(1);
    const [profile, setProfile] = useState<any>("");
    const [loadingPubs, setLoadingPubs] = useState<boolean>(false);
    const [publicationsAux, setPublicationsAux] = useState<any>([]);
    const [onPausePublication, setOnPausePublication] = useState<any>("");
    const [totalPublications, setTotalPublications] = useState<number>(1);

    const matches = useMediaQuery('(max-width:1024px)');
    const [isSmallScreen, setIsSmallScreen] = useState(matches);

    useEffect(() => {
        setIsSmallScreen(matches);
    }, [matches]);

    const [modalPause, setModalPause] = useState<any>({ open: false, publication: "" });
    const [openModalInstructions, setOpenModalInstructions] = useState<boolean>(false);
    const [openModalDetails, setOpenModalDetails] = useState<boolean>(false);
    const [currentModalStep, setCurrentModalStep] = useState<number>(1);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [publicationToDelete, setPublicationToDelete] = useState<any>(null);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const handlePressRow = (item: any) => {
        navigate(`/perfil/mis_publicaciones/${item?._id}/gestion`);
    }

    const onSettings = (item: any, event: any) => {
        event.stopPropagation();
        navigate(`/perfil/mis_publicaciones/${item?._id}/editar`);
    }

    const onPause = (event: React.MouseEvent<HTMLButtonElement>, item: any) => {
        event.stopPropagation();
        setOnPausePublication(item);
        pausePublication(item?._id, !item?.paused);
    }

    const handleDeletePublication = (publication: any) => {
        setPublicationToDelete(publication);
        setOpenDeleteModal(true);
    }

    const handleConfirmDelete = (publicationId: string) => {
        deletePublication(publicationId);
    }

    useEffect(() => {
        if (deleteFetching === "success") {
            setOpenDeleteModal(false);
            toast("Publicación eliminada exitosamente");
            getMyPublicationsRequest(page);
        }
        if (deleteFetching === "failure") {
            toast("La publicación no se puede eliminar ya que se encuentra en una transacción");
        }
    }, [deleteFetching]);

    useEffect(() => {
        if (pauseFetching === "success" && onPausePublication) {
            setPublicationsAux((prevPublications: any[]) =>
                prevPublications.map((pub: any) =>
                    pub._id === onPausePublication._id ? { ...pub, paused: pause.paused } : pub
                )
            );
            setOnPausePublication(null);
        }
        if (pauseFetching === "failure") {
            toast("No se puede pausar la publicación mientras esté en una transacción.");
        }
    }, [pauseFetching, pause.paused, onPausePublication]);

    useEffect(() => {
        if (myPublications?.fetching === "loading" && publicationsAux?.length > 0) {
            setLoadingPubs(true);
        }
        if (myPublications?.fetching === "failure") {
            toast("Ha ocurrido un error al mostrar las publicaciones");
        }
        if (myPublications?.fetching === "success") {
            setPublicationsAux(myPublications?.data);
            setTotalPublications(Math.ceil(myPublications?.total / 20));
            setLoadingPubs(false);

            if (myPublications?.data?.length === 0) {
                setOpenModalInstructions(true);
                setCurrentModalStep(1);
            }
        }
    }, [myPublications?.fetching]);

    useEffect(() => {
        getMyPublicationsRequest(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [location, page]);

    useEffect(() => {
        setProfile(profileRole(user?.profile?.code));
    }, []);

    return (
        params.id
            ?
            <Outlet />
            :
            <>
                <DeletePublicationModal
                    open={openDeleteModal}
                    setOpen={setOpenDeleteModal}
                    publication={publicationToDelete}
                    onDeleteConfirm={handleConfirmDelete}
                    loading={false}
                />
                <div className={styles.myPublicationsContainer}>
                    <div className={styles.titleList}>
                        <h2 className={styles.title}>Mis publicaciones</h2>
                    </div>

                    {
                        myPublications?.fetching === "loading"
                            ?
                            <div className={styles.loaderPublications} id="loaderPublications">
                                <CircularProgress
                                    size={styles.loader}
                                    classes={{
                                        colorPrimary: styles.loader
                                    }}
                                />
                                <p className={styles.text}>Cargando publicaciones</p>
                            </div>
                            :
                            <>
                                {publicationsAux?.map((item: any, index: number) => (
                                    <MyPublicationCard
                                        key={item._id || index}
                                        publication={item}
                                        visits={item?.views || 0}
                                        confirmedVisits={item?.confirmed_visits || 0}
                                        interactions={item?.interactions || 0}
                                        isFavorited={item?.liked || false}
                                        isPaused={item?.paused || false}
                                        onClick={() => handlePressRow(item)}
                                        onEdit={() => onSettings(item, new MouseEvent('click') as unknown as React.MouseEvent<HTMLButtonElement>)}
                                        onPause={(publicationId: string) => onPause(new MouseEvent('click') as unknown as React.MouseEvent<HTMLButtonElement>, item)}
                                        onDeleteClick={(publication: any) => handleDeletePublication(publication)}
                                        onFavoritedChange={(isFavorited: boolean) => console.log('Favorited changed:', isFavorited)}
                                    />
                                ))}

                                {
                                    (publicationsAux && publicationsAux.length > 0) &&
                                    <Pagination
                                        page={page}
                                        setPage={setPage}
                                        className={styles.pagination}
                                        total={totalPublications}
                                    />
                                }
                            </>
                    }
                </div>
            </>
    )
};

export default EntryPoint;